import { useEffect, useState } from "react";
import { api, errDetail, fmtIST } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Category -> badge tint. Purely presentational; the category itself is
// decided server-side (backend/case_timeline.py).
const CAT_CLS = {
  stage: "bg-[hsl(var(--info))]/10 text-[hsl(var(--info))]",
  status: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]",
  outcome: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]",
  assignment: "bg-primary/10 text-primary",
  priority: "bg-[hsl(var(--danger))]/10 text-[hsl(var(--danger))]",
};

/**
 * The ONE unified Case history feed (GET /cases/{id}/timeline — notes,
 * communications, tasks, documents & information requests, reassignments,
 * priority, waiting/at-risk, stage moves, workflow outcomes, tickets,
 * payments, approvals and any other audited Case event, de-duplicated and
 * newest first). Used by the Case page's Timeline tab AND Customer 360's
 * Process tab, so the two can never render the same history differently.
 *
 * `initial` — an already-loaded first page (Customer 360 receives it in its
 * own payload), used instead of a fetch while on page 1.
 * `page`/`onPageChange` — optional controlled page (the Case page keeps it in
 * the URL via useUrlState); uncontrolled otherwise.
 */
export function CaseTimelineFeed({ caseId, initial, page: controlledPage, onPageChange,
  pageSize = 25, testId = "case-timeline" }) {
  const [ownPage, setOwnPage] = useState(1);
  const page = controlledPage || ownPage;
  const setPage = onPageChange || setOwnPage;
  const [feed, setFeed] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (page === 1 && initial && initial.page_size === pageSize) {
      setFeed(initial); setError(null); return undefined;
    }
    let live = true;
    setFeed(null);
    api.get(`/cases/${caseId}/timeline`, { params: { page, page_size: pageSize, order: "desc" } })
      .then((r) => { if (live) { setFeed(r.data); setError(null); } })
      .catch((e) => { if (live) setError(errDetail(e, "Could not load the Case timeline")); });
    return () => { live = false; };
  }, [caseId, page, pageSize, initial]);

  if (error) return <p className="text-xs text-[hsl(var(--danger))]" data-testid={`${testId}-error`}>{error}</p>;
  if (!feed) return <Skeleton className="h-24" />;
  if (feed.total === 0) {
    return <p className="text-xs text-muted-foreground" data-testid={`${testId}-empty`}>Nothing recorded on this Case yet.</p>;
  }

  return (
    <div className="space-y-2 min-w-0" data-testid={testId}>
      <ol className="space-y-2">
        {feed.rows.map((r) => (
          <li key={r.event_id} data-testid={`${testId}-row`} data-event-id={r.event_id}
            data-event-type={r.event_type}
            className="flex flex-col sm:flex-row sm:items-start gap-0.5 sm:gap-2 min-w-0 border-b last:border-b-0 pb-1.5">
            <span className="font-mono text-[11px] text-muted-foreground shrink-0 sm:w-40">
              {fmtIST(r.at, { year: true })}
            </span>
            <div className="min-w-0 flex-1 space-y-0.5">
              <div className="flex items-start gap-1.5 flex-wrap min-w-0">
                <Badge variant="outline" className={`text-[9px] capitalize shrink-0 ${CAT_CLS[r.category] || ""}`}>
                  {String(r.category || "event").replace(/_/g, " ")}
                </Badge>
                <span className="text-xs font-medium break-words min-w-0">{r.title}</span>
              </div>
              <div className="text-[11px] text-muted-foreground break-words">
                <span>by {r.actor_name}</span>
                {r.reason && !(r.details || "").includes(r.reason) && <span> — {r.reason}</span>}
              </div>
              {r.details && r.details !== r.title && (
                <p className="text-[11px] text-muted-foreground break-words">{r.details}</p>
              )}
            </div>
          </li>
        ))}
      </ol>
      <div className="flex items-center gap-2 flex-wrap text-[11px] text-muted-foreground" data-testid={`${testId}-pager`}>
        <span>{feed.total} event{feed.total === 1 ? "" : "s"} · page {feed.page} of {feed.pages}</span>
        {feed.truncated && <span className="text-[hsl(var(--warning))]">(very long history — oldest entries capped)</span>}
        {feed.pages > 1 && (
          <span className="flex items-center gap-1 ml-auto">
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={feed.page <= 1}
              data-testid={`${testId}-newer`} onClick={() => setPage(Math.max(1, feed.page - 1))}>
              <ChevronLeft className="h-3.5 w-3.5 mr-0.5" />Newer
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={feed.page >= feed.pages}
              data-testid={`${testId}-older`} onClick={() => setPage(feed.page + 1)}>
              Older<ChevronRight className="h-3.5 w-3.5 ml-0.5" />
            </Button>
          </span>
        )}
      </div>
    </div>
  );
}
